<!-- src/views/Restaurante/MenusList.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">

    <!-- HEADER -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-10 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-purple-600 dark:text-purple-400 tracking-widest uppercase">Módulo Restaurante</span>
        </div>
        <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-3">
          Menús
          <span class="px-3 py-1 bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 text-xs sm:text-sm font-black rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            {{ menus.length }}
          </span>
        </h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Administra los menús del restaurante</p>
      </div>

      <div class="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
        <button @click="openCreateModal"
          class="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-black text-xs sm:text-sm tracking-wide transition-all shadow-lg shadow-purple-500/30 flex items-center justify-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" />
          <span>Nuevo Menú</span>
        </button>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6 animate-fadeInUp">
      <div class="flex flex-col sm:flex-row gap-4 items-end">
        <div class="flex-1 w-full">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Buscar Menús</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input v-model="searchQuery" type="text" placeholder="Buscar por nombre o descripción..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-purple-500 rounded-xl transition-all outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>
        <div class="w-full sm:w-48">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Estado</label>
          <select v-model="filterStatus"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-purple-500 rounded-xl font-bold text-gray-700 dark:text-gray-200 outline-none text-sm">
            <option value="all">Todos</option>
            <option value="activos">Solo Activos</option>
            <option value="inactivos">Solo Inactivos</option>
          </select>
        </div>
        <button @click="cargarMenus"
          class="w-full sm:w-auto px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs hover:bg-gray-200 transition-all flex items-center justify-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- CONTENIDO -->
    <div class="animate-fadeInUp delay-100">
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-if="loading" v-for="i in 3" :key="'load-'+i"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 animate-pulse h-52"></div>

        <div v-if="!loading && menusFiltrados.length === 0"
          class="col-span-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-10 text-center">
          <div class="flex flex-col items-center opacity-40 select-none">
            <Icon icon="mdi:book-open-variant" class="text-6xl mb-3 text-gray-300" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest uppercase">No hay menús</h4>
            <p class="text-sm text-gray-400 mt-1">Crea tu primer menú para empezar</p>
          </div>
        </div>

        <div v-for="menu in menusFiltrados" :key="menu.idMenu"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col group">
          <div class="p-5 flex-1">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center gap-3">
                <div :class="['w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border',
                  menu.estado === 'activo'
                    ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100 dark:from-purple-900/40 dark:to-pink-900/40 dark:border-purple-800/50'
                    : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                  <Icon icon="mdi:book-open-variant"
                    :class="['text-2xl', menu.estado === 'activo' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400']" />
                </div>
                <div>
                  <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="font-black text-gray-900 dark:text-white text-base">{{ menu.nombre }}</h3>
                    <span v-if="menu.es_principal"
                      class="px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-md text-[9px] font-black uppercase tracking-widest shadow-sm flex items-center gap-0.5">
                      <Icon icon="mdi:star" class="text-xs" /> Principal
                    </span>
                  </div>
                  <p class="text-xs font-bold text-gray-400 mt-0.5">ID: {{ menu.idMenu }}</p>
                </div>
              </div>
              <span :class="['px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border',
                menu.estado === 'activo'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                  : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50']">
                {{ convertirEstadoATexto(menu.estado) }}
              </span>
            </div>

            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 min-h-[2.5rem]">
              {{ menu.descripcion || 'Sin descripción' }}
            </p>

            <div class="grid grid-cols-2 gap-3">
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Inicio</p>
                <p class="text-[11px] font-bold text-gray-700 dark:text-gray-300 mt-1">{{ formatearFecha(menu.fecha_inicio) }}</p>
              </div>
              <div class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700/50">
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Fin</p>
                <p class="text-[11px] font-bold text-gray-700 dark:text-gray-300 mt-1">{{ formatearFecha(menu.fecha_fin) }}</p>
              </div>
            </div>
          </div>

          <div class="bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 p-3 grid grid-cols-3 gap-2">
            <button @click.stop="irADetalle(menu)"
              class="col-span-3 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-1 hover:opacity-90 transition-all shadow-sm">
              <Icon icon="mdi:view-list" class="text-base" />
              Gestionar Categorías y Productos
            </button>
            <button @click.stop="openEditModal(menu)"
              class="py-2.5 bg-white dark:bg-gray-800 text-amber-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-amber-50 transition-colors">
              <Icon icon="mdi:pencil" class="text-base sm:text-sm" /><span>Editar</span>
            </button>
            <button @click.stop="abrirModalToggleStatus(menu)"
              class="py-2.5 bg-white dark:bg-gray-800 text-indigo-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-indigo-50 transition-colors">
              <Icon :icon="menu.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-base sm:text-sm" />
              <span>{{ menu.estado === 'activo' ? 'Desactivar' : 'Activar' }}</span>
            </button>
            <button @click.stop="abrirModalEliminar(menu)"
              class="py-2.5 bg-white dark:bg-gray-800 text-rose-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-rose-50 transition-colors">
              <Icon icon="mdi:delete" class="text-base sm:text-sm" /><span>Eliminar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL FORMULARIO MENÚ -->
    <Teleport to="body">
      <div v-if="showModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingMenu ? 'mdi:pencil-box' : 'mdi:book-open-variant'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingMenu ? 'Editar Menú' : 'Nuevo Menú' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingMenu ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeModal" class="ml-auto p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>

          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveMenu" class="space-y-4">
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Nombre *</label>
                <input v-model="form.nombre" type="text" required maxlength="100"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none"
                  placeholder="Ej: Menú Principal, Menú del Día" />
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Descripción</label>
                <textarea v-model="form.descripcion" rows="2" maxlength="200"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none resize-none"
                  placeholder="Descripción opcional..."></textarea>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Fecha inicio</label>
                  <input v-model="form.fecha_inicio" type="date"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Fecha fin</label>
                  <input v-model="form.fecha_fin" type="date"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-purple-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none" />
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-3 uppercase">Opciones</label>
                <div class="space-y-2">
                  <label class="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" v-model="form.es_principal" class="w-5 h-5 rounded text-purple-600 focus:ring-purple-500" />
                    <div>
                      <span class="text-sm font-black text-gray-700 dark:text-gray-200">Menú Principal</span>
                      <p class="text-[10px] text-gray-400 font-medium">Se mostrará como el menú por defecto</p>
                    </div>
                  </label>
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-3 uppercase">Estado</label>
                <div class="grid grid-cols-2 gap-3">
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer transition-all',
                    form.estado === 'activo' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/30' : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" value="activo" class="hidden" /> Activo
                  </label>
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer transition-all',
                    form.estado === 'inactivo' ? 'bg-rose-50 border-rose-500 text-rose-700 dark:bg-rose-900/30' : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" value="inactivo" class="hidden" /> Inactivo
                  </label>
                </div>
              </div>

              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">
                  Cancelar
                </button>
                <button type="submit" :disabled="saving"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-purple-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" class="text-lg" />
                  {{ saving ? 'Guardando...' : (editingMenu ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <div v-if="showConfirmModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion"
              class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all">
              Cancelar
            </button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando"
              class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg"
              :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2';
import {
  obtenerMenus, crearMenu, actualizarMenu, eliminarMenu,
  formatearFecha, convertirEstadoATexto,
} from '@/api/menus.js';

export default {
  name: 'MenusList',
  components: { Icon },
  data() {
    return {
      menus: [], loading: false, saving: false, showModal: false,
      searchQuery: '', filterStatus: 'all',
      form: { nombre: '', descripcion: '', fecha_inicio: '', fecha_fin: '', es_principal: false, estado: 'activo' },
      editingMenu: null,
      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    menusFiltrados() {
      let arr = [...this.menus];
      if (this.searchQuery.trim()) {
        const t = this.searchQuery.toLowerCase();
        arr = arr.filter(m =>
          m.nombre?.toLowerCase().includes(t) ||
          m.descripcion?.toLowerCase().includes(t));
      }
      if (this.filterStatus === 'activos') arr = arr.filter(m => m.estado === 'activo');
      else if (this.filterStatus === 'inactivos') arr = arr.filter(m => m.estado === 'inactivo');
      // Principales primero
      return arr.sort((a, b) => Number(b.es_principal) - Number(a.es_principal));
    },
  },
  mounted() { this.cargarMenus(); },
  methods: {
    mostrarToast(icon, title, text = '') {
      Swal.fire({ toast: true, position: 'top-end', icon, title, text, showConfirmButton: false, timer: 3000, timerProgressBar: true });
    },
    async cargarMenus() {
      this.loading = true;
      try {
        const r = await obtenerMenus();
        if (r.success) this.menus = r.data || [];
      } finally { this.loading = false; }
    },
    irADetalle(menu) {
      this.$router.push({ name: 'MenuDetalle', params: { id: menu.idMenu } });
    },
    openCreateModal() {
      this.editingMenu = null;
      this.form = { nombre: '', descripcion: '', fecha_inicio: '', fecha_fin: '', es_principal: false, estado: 'activo' };
      this.showModal = true;
    },
    openEditModal(m) {
      this.editingMenu = m;
      this.form = {
        nombre: m.nombre, descripcion: m.descripcion || '',
        fecha_inicio: m.fecha_inicio ? m.fecha_inicio.split('T')[0] : '',
        fecha_fin: m.fecha_fin ? m.fecha_fin.split('T')[0] : '',
        es_principal: !!m.es_principal,
        estado: m.estado,
      };
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    async saveMenu() {
      this.saving = true;
      try {
        const result = this.editingMenu
          ? await actualizarMenu(this.editingMenu.idMenu, this.form)
          : await crearMenu(this.form);
        if (result.success) {
          await this.cargarMenus();
          this.closeModal();
          this.mostrarToast('success', '¡Éxito!', `Menú ${this.editingMenu ? 'actualizado' : 'creado'} correctamente.`);
        } else {
          this.mostrarToast('error', 'Error', result.error || 'Error al guardar');
        }
      } finally { this.saving = false; }
    },
    abrirModalEliminar(m) {
      this.abrirModalConfirmacion({
        titulo: 'Desactivar Menú', subtitulo: 'Cambiará a inactivo',
        mensaje: `¿Desactivar el menú "${m.nombre}"?`,
        detalle: 'El menú se marcará como inactivo.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600',
        botonTexto: 'Desactivar', datos: m.idMenu,
        accion: (id) => this.deleteMenu(id),
      });
    },
    async deleteMenu(id) {
      const r = await eliminarMenu(id);
      if (r.success) {
        await this.cargarMenus();
        this.mostrarToast('success', 'Desactivado', 'Menú desactivado correctamente.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'No se pudo desactivar.');
      }
    },
    abrirModalToggleStatus(m) {
      const esActivo = m.estado === 'activo';
      this.abrirModalConfirmacion({
        titulo: esActivo ? 'Desactivar Menú' : 'Activar Menú',
        subtitulo: 'Cambiar estado del menú',
        mensaje: `¿${esActivo ? 'desactivar' : 'activar'} "${m.nombre}"?`,
        detalle: `Pasará de "${convertirEstadoATexto(m.estado)}" a "${convertirEstadoATexto(esActivo ? 'inactivo' : 'activo')}".`,
        icon: esActivo ? 'mdi:close-circle' : 'mdi:check-circle',
        color: esActivo ? 'bg-gradient-to-r from-orange-500 to-amber-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        buttonColor: esActivo ? 'bg-gradient-to-r from-orange-500 to-amber-600' : 'bg-gradient-to-r from-green-600 to-emerald-600',
        botonTexto: esActivo ? 'Desactivar' : 'Activar',
        datos: m, accion: (menu) => this.toggleStatus(menu),
      });
    },
    async toggleStatus(m) {
      const esActivo = m.estado === 'activo';
      const result = await actualizarMenu(m.idMenu, { ...m, estado: esActivo ? 'inactivo' : 'activo' });
      if (result.success) {
        await this.cargarMenus();
        this.mostrarToast('success', 'Actualizado', `Menú ${esActivo ? 'desactivado' : 'activado'}.`);
      } else {
        this.mostrarToast('error', 'Error', result.error || 'Error al cambiar estado.');
      }
    },
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-purple-600 to-pink-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-purple-600 to-pink-600';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },
    formatearFecha, convertirEstadoATexto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@media (max-width: 768px) { button { min-height: 44px; } input, select, textarea { font-size: 16px !important; } }
</style>